"use client";

import { useEffect, useState } from "react";
import { JobListPanel } from "@/components/JobListPanel";
import type { JobListResult } from "@/lib/queries";

/**
 * Client-side pager for the programmatic list templates (org/region/country/
 * city/category pages). The route itself always server-renders page 1 (so it
 * can be ISR-cached instead of force-dynamic — see the CM-163 comment on each
 * page.tsx for why reading `searchParams` server-side broke that). This
 * fetches other pages via a bound server action after mount and swaps the URL
 * with pushState so copy-paste/back-forward still work, without a server
 * round trip through the route itself.
 *
 * Every paginated variant already canonicalizes to the page-1 URL (see each
 * page's generateMetadata), so a crawler landing directly on `?page=2`
 * seeing page-1 content until JS runs is consistent with that signal, not a
 * regression.
 */
export function PagedJobList({
  initial,
  basePath,
  emptyLabel,
  now,
  fetchPage,
}: {
  initial: JobListResult;
  basePath: string;
  emptyLabel: string;
  now: Date;
  fetchPage: (page: number) => Promise<JobListResult>;
}) {
  const [result, setResult] = useState(initial);

  // Deep link straight to page 2+ (e.g. an old bookmarked/shared URL) —
  // fetch the requested page once on mount instead of showing page 1.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("page");
    const requested = raw ? Number.parseInt(raw, 10) : 1;
    if (Number.isFinite(requested) && requested > 1 && requested !== initial.page) {
      fetchPage(requested)
        .then(setResult)
        .catch(() => {});
    }
    // Intentionally mount-only — goToPage handles every later change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToPage = (page: number) => {
    const url = page <= 1 ? basePath : `${basePath}?page=${page}`;
    window.history.pushState(null, "", url);
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchPage(page)
      .then(setResult)
      .catch(() => {});
  };

  return (
    <JobListPanel
      result={result}
      basePath={basePath}
      emptyLabel={emptyLabel}
      now={now}
      onPageChange={goToPage}
    />
  );
}
